"use client";

import { useState, type CSSProperties } from "react";
import { cn } from "@newxsystems/shared";
import { resolveTootPrimitive } from "../../../../client";

type TootPlayableEmbedProps = {
  alt?: string;
  embedTitle?: string;
  height?: number | `${number}`;
  loading?: "eager" | "lazy";
  objectPosition?: string;
  poster: string;
  sizes?: string;
  src: string;
  srcSet?: string;
  width?: number | `${number}`;
};

const getAutoplayEmbedUrl = (url: string) => `${url}${url.includes("?") ? "&" : "?"}autoplay=1`;

export const TootPlayableEmbed = ({
  alt = "",
  embedTitle,
  height,
  loading = "lazy",
  objectPosition,
  poster,
  sizes,
  src,
  srcSet,
  width,
}: TootPlayableEmbedProps) => {
  const Image = resolveTootPrimitive("Image");
  const [isPlaying, setIsPlaying] = useState(false);
  const imageStyle: CSSProperties | undefined = objectPosition ? { objectPosition } : undefined;
  const title = embedTitle ?? "video";

  if (isPlaying) {
    return (
      <iframe
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
        className="relative z-10 h-full w-full border-0"
        loading={loading}
        referrerPolicy="strict-origin-when-cross-origin"
        src={getAutoplayEmbedUrl(src)}
        title={embedTitle}
      />
    );
  }

  return (
    <button
      aria-label={`Play ${title}`}
      className="group relative z-10 block h-full w-full cursor-pointer overflow-hidden text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-4px] focus-visible:outline-[var(--color-toot-orange)]"
      onClick={() => setIsPlaying(true)}
      type="button"
    >
      <Image
        alt={alt}
        className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 ease-out motion-reduce:transition-none motion-safe:group-hover:scale-[1.025]"
        draggable={false}
        height={height}
        loading={loading}
        sizes={sizes}
        src={poster}
        srcSet={srcSet}
        style={imageStyle}
        width={width}
      />
      <span
        aria-hidden="true"
        className="absolute inset-0 bg-[linear-gradient(180deg,rgba(5,5,6,0.08)_0%,rgba(5,5,6,0.28)_48%,rgba(5,5,6,0.84)_100%)]"
      />
      <span
        aria-hidden="true"
        className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-[var(--color-toot-red)] to-transparent opacity-80"
      />
      <span className="absolute left-1/2 top-1/2 inline-flex -translate-x-1/2 -translate-y-1/2 items-center gap-3 rounded-full border border-orange-200/30 bg-[var(--color-toot-red)] px-3 py-3 text-sm font-black uppercase tracking-[0.12em] text-[var(--color-primary-foreground)] shadow-[0_1rem_3rem_color-mix(in_oklab,var(--color-toot-red)_38%,transparent)] transition-[background-color,box-shadow,transform] duration-200 group-hover:bg-[var(--color-toot-orange)] group-hover:shadow-[0_1.25rem_3.5rem_color-mix(in_oklab,var(--color-toot-orange)_34%,transparent)] motion-reduce:transition-none motion-safe:group-hover:scale-[1.02] sm:px-5">
        <span className="grid size-10 place-items-center rounded-full bg-[var(--color-primary-foreground)] text-[var(--color-toot-red)] shadow-[inset_0_0_0_1px_rgba(255,255,255,0.18)] sm:size-11">
          <span className="ml-1 h-0 w-0 border-y-[7px] border-l-[12px] border-y-transparent border-l-current" />
        </span>
        <span className="hidden sm:inline">Play video</span>
      </span>
      {embedTitle ? (
        <span
          className={cn(
            "absolute left-3 right-3 top-3 max-w-[calc(100%-1.5rem)] rounded-[var(--radius-toot-frame)] border border-toot-grid-strong/65 bg-toot-surface-terminal/78 px-3 py-2 text-toot-foreground shadow-[0_1rem_2.5rem_rgba(0,0,0,0.32)] backdrop-blur-sm",
            "sm:left-5 sm:right-auto sm:top-5 sm:max-w-[min(28rem,calc(100%-2.5rem))] sm:px-4 sm:py-3",
          )}
        >
          <span className="block truncate text-xs font-black uppercase tracking-[0.18em] text-toot-orange sm:text-sm">
            {embedTitle}
          </span>
        </span>
      ) : null}
    </button>
  );
};
